// Pure helpers for Provider storefront pages. A storefront is ONE Page Builder document (same model
// as a Seller shop) whose doc.pages[] are the Provider's pages; the Provider-specific page settings
// (Home, navigation, publication) come from the storefront API (`pages` rows).

const EMPTY_PAGE_STYLE = { minHeight: "60vh", padding: "40px 20px", background: "#ffffff" };

let seq = 0;
export const newId = (prefix = "n") => `${prefix}-${Date.now().toString(36)}${(seq++).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const pagesOf = (doc) => (Array.isArray(doc?.pages) ? doc.pages.filter((p) => p && p.id != null) : []);

// Pages listed in the storefront navigation, in the Provider's order.
export const navigationPages = (rows = []) => rows.filter((r) => r.showInNavigation !== false).sort((a, b) => (a.navigationOrder ?? 0) - (b.navigationOrder ?? 0));

// Which page to render: the requested one when it exists in the document, else Home, else the first.
export const resolveActivePageId = (doc, rows = [], requestedId = null, homePageId = null) => {
  const ids = pagesOf(doc).map((p) => String(p.id));
  if (requestedId && ids.includes(String(requestedId))) return String(requestedId);
  const home = homePageId || rows.find((r) => r.isHome)?.id;
  if (home && ids.includes(String(home))) return String(home);
  const firstNav = navigationPages(rows).find((r) => ids.includes(String(r.id)));
  return firstNav ? String(firstNav.id) : (ids[0] || null);
};

const walk = (node, visit) => {
  if (!node || typeof node !== "object") return;
  visit(node);
  (Array.isArray(node.children) ? node.children : []).forEach((c) => walk(c, visit));
};

// True when the page already places Provider products itself (a product grid element), so the
// automatic catalogue must not render the same products a second time.
export const pageHasProductBlock = (page) => {
  let found = false;
  (page?.children || []).forEach((c) => walk(c, (n) => { if (n.type === "shopProductGrid") found = true; }));
  return found;
};

// Deep copy of a page with brand-new element ids (unique across the storefront document, which the
// Page Builder requires); any string equal to an old id (anchors, toggle targets...) is remapped too.
export const clonePageWithNewIds = (page, { name } = {}) => {
  const map = new Map();
  (page?.children || []).forEach((c) => walk(c, (n) => { if (n.id != null) map.set(String(n.id), newId("el")); }));
  const remap = (value) => {
    if (Array.isArray(value)) return value.map(remap);
    if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, remap(v)]));
    if (typeof value === "string" && map.has(value)) return map.get(value);
    return value;
  };
  const children = remap(JSON.parse(JSON.stringify(page?.children || [])));
  return { ...JSON.parse(JSON.stringify(page || {})), id: newId("page"), name: name || page?.name || "Page", children };
};

export const blankPage = (name) => ({ id: newId("page"), name, style: { ...EMPTY_PAGE_STYLE }, children: [] });

// First page of a Page Builder template (Classic, Premium...) as a NEW page of the Provider: the
// global template itself is never modified.
export const pageFromTemplate = (template, name) => {
  const doc = typeof template?.make === "function" ? template.make() : null;
  const first = pagesOf(doc)[0];
  if (!first) return { page: blankPage(name), meta: null };
  return { page: clonePageWithNewIds(first, { name }), meta: doc.meta || null };
};

export const emptyStorefrontDoc = (meta = null) => ({ meta: meta || {}, pageMode: "store", pages: [], comments: [], combinedElements: [] });

export const addPage = (doc, page, meta = null) => {
  const base = doc && Array.isArray(doc.pages) ? doc : emptyStorefrontDoc(meta);
  return { ...base, meta: pagesOf(base).length ? base.meta : (meta || base.meta || {}), pages: [...pagesOf(base), page], currentPageId: page.id };
};

export const removePage = (doc, pageId) => {
  const pages = pagesOf(doc).filter((p) => String(p.id) !== String(pageId));
  return { ...doc, pages, currentPageId: pages[0]?.id || null };
};

export const renamePage = (doc, pageId, name) => ({
  ...doc,
  pages: pagesOf(doc).map((p) => (String(p.id) === String(pageId) ? { ...p, name } : p)),
});

export const duplicatePage = (doc, pageId, name) => {
  const source = pagesOf(doc).find((p) => String(p.id) === String(pageId));
  if (!source) return doc;
  const copy = clonePageWithNewIds(source, { name });
  const pages = pagesOf(doc);
  const at = pages.findIndex((p) => String(p.id) === String(pageId));
  return { ...doc, pages: [...pages.slice(0, at + 1), copy, ...pages.slice(at + 1)], currentPageId: copy.id };
};

// Template sample statistics ("28", "100%", "4.8/5"...) are placeholders of the Seller shop
// templates, never real Provider figures: a Provider storefront hides them (unbound values only).
export const TEMPLATE_SAMPLE_STAT_VALUES = ["28", "100%", "4.8/5", "100% Naturel"];
// "Unbound" = no binding at all (a binding resolved by the renderer leaves bindKey: null + _resolvedValue).
export const isUnboundNode = (node) => node?.bindKey === undefined && !node?.bindSource && node?._resolvedValue === undefined;
export const isTemplateSampleStat = (node) => node?.type === "stat" && isUnboundNode(node)
  && TEMPLATE_SAMPLE_STAT_VALUES.includes(String(node?.content?.value ?? "").trim());
